import React, { useEffect } from "react";
import {
  Grid,
  Typography,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Divider,
} from "@mui/material";
import { useFetch } from "services/hooks/useFetch";
import { useSelector } from "react-redux";
import { usePayrollCycleRun } from "../../PayrollCycleRunContext";
import { themes } from "services/constants";
import { stickyHeader } from "services/constants";
import { Loader } from "components/Loader";

// Sub-components
const DataTable = ({
  data,
  columns,
  columnWidths = [],
  columnAlign = [],
  emptyMessage = "No data available",
  renderRow,
  summaryRow,
  showAddWhenEmpty = false,
}) => (
  <TableContainer sx={{ mb: 2.5, backgroundColor: themes?.whiteColor }}>
    <Table size="small" className="border" sx={{ tableLayout: "fixed" }}>
      <TableHead sx={stickyHeader}>
        <TableRow>
          {columns.map((column, index) => (
            <TableCell
              key={index}
              align={columnAlign[index] || "left"}
              sx={{
                width: columnWidths[index] || "auto",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {column}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      {!data || data.length === 0 ? (
        <TableBody>
          {showAddWhenEmpty ? (
            renderRow(null, 0, true)
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                <Alert severity="info">{emptyMessage}</Alert>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      ) : (
        <TableBody>
          {data.map((item, index) => renderRow(item, index))}
          {summaryRow && summaryRow()}
        </TableBody>
      )}
    </Table>
  </TableContainer>
);

export const PayslipDetails = () => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const { setAttendanceData, userId, payScheduleId } = usePayrollCycleRun();
  const { result: attendanceDetails, loading } = useFetch(
    `${domain}payroll/operations/payrollprocessing/payrollruncycle/individual-employee-payrun/?pay_schedule_id=${payScheduleId}&user_id=${userId}`
  );

  useEffect(() => {
    if (!loading && attendanceDetails) {
      setAttendanceData(attendanceDetails);
    }
  }, [attendanceDetails, loading, setAttendanceData]);

  if (attendanceDetails?.status === "Draft") {
    return (
      <Alert severity="info" fullWidth>
        Payroll run has not been initiated to the user yet.
      </Alert>
    );
  }

  return loading ? (
    <Loader />
  ) : (
    <>
      <Grid container sx={{ p: 1, backgroundColor: themes?.primary }}>
        <Grid container spacing={1} sx={{ py: 1 }}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Total Payable Days{" "}
              <strong>
                {attendanceDetails?.payslip?.total_payable_days ?? "-"}
              </strong>
            </Typography>
          </Grid>
          <Grid item xs={3}></Grid>

          <Grid item xs={3}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ alignItems: "flex-end" }}
            >
              Salary{" "}
              <strong>
                {attendanceDetails?.payslip?.monthly_salary ?? "-"}
              </strong>
            </Typography>
          </Grid>
        </Grid>

        <DataTable
          data={attendanceDetails?.payslip?.earnings}
          columns={[<>Earnings</>, "Amount", ""]}
          columnWidths={["60%", "30%", "10%"]}
          columnAlign={["left", "right", "left"]}
          renderRow={(data, index) => (
            <React.Fragment key={index}>
              <TableRow>
                <TableCell>{data?.name}</TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  ₹{data?.amount}
                </TableCell>
                <TableCell />
              </TableRow>
            </React.Fragment>
          )}
          summaryRow={() => (
            <>
              <TableRow>
                <TableCell>
                  <strong>Total</strong>
                </TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  <strong>₹{attendanceDetails?.payslip?.total_earnings}</strong>
                </TableCell>
                <TableCell />
              </TableRow>
            </>
          )}
        />

        <DataTable
          data={attendanceDetails?.payslip?.other_earnings}
          columns={[<>Other Earnings</>, "Amount", ""]}
          columnWidths={["60%", "30%", "10%"]}
          columnAlign={["left", "right", "left"]}
          renderRow={(data, index) => (
            <React.Fragment key={index}>
              <TableRow>
                <TableCell>{data?.name}</TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  ₹{data?.amount}
                </TableCell>
                <TableCell />
              </TableRow>
            </React.Fragment>
          )}
          summaryRow={() => (
            <>
              <TableRow>
                <TableCell>
                  <strong>Total</strong>
                </TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  <strong>
                    ₹{attendanceDetails?.payslip?.total_other_earnings}
                  </strong>
                </TableCell>
                <TableCell />
              </TableRow>
            </>
          )}
        />

        <DataTable
          data={attendanceDetails?.payslip?.deductions}
          columns={[<>Deductions</>, "Amount", ""]}
          columnWidths={["60%", "30%", "10%"]}
          columnAlign={["left", "right", "left"]}
          renderRow={(data, index) => (
            <React.Fragment key={index}>
              <TableRow>
                <TableCell>{data?.name}</TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  ₹{data?.amount}
                </TableCell>
                <TableCell />
              </TableRow>
            </React.Fragment>
          )}
          summaryRow={() => (
            <>
              <TableRow>
                <TableCell>
                  <strong>Total</strong>
                </TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  <strong>
                    ₹{attendanceDetails?.payslip?.total_deductions}
                  </strong>
                </TableCell>
                <TableCell />
              </TableRow>
            </>
          )}
        />

        <DataTable
          data={attendanceDetails?.payslip?.additional_deduction}
          columns={[<>Other Deductions</>, "Amount", ""]}
          columnWidths={["60%", "30%", "10%"]}
          columnAlign={["left", "right", "left"]}
          renderRow={(data, index) => (
            <React.Fragment key={index}>
              <TableRow>
                <TableCell>{data?.name}</TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  ₹{data?.amount}
                </TableCell>
                <TableCell />
              </TableRow>
            </React.Fragment>
          )}
          summaryRow={() => (
            <>
              <TableRow>
                <TableCell>
                  <strong>Total</strong>
                </TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  <strong>
                    ₹{attendanceDetails?.payslip?.total_additional_deductions}
                  </strong>
                </TableCell>
                <TableCell />
              </TableRow>
            </>
          )}
        />

        <Box
          sx={{
            padding: 2,
            borderRadius: 2,
            width: "100%",
          }}
        >
          {[
            {
              label: "Gross Salary",
              value: attendanceDetails?.payslip?.gross_salary,
            },
            {
              label: "Deduction",
              value: attendanceDetails?.payslip?.total_deductions,
            },
            {
              label: "Net Salary",
              value: attendanceDetails?.payslip?.net_salary,
            },
            {
              label: "Other Deduction",
              value: attendanceDetails?.payslip?.total_additional_deductions,
            },
            {
              label: "Net Pay",
              value: attendanceDetails?.payslip?.net_pay,
            },
          ].map((row, i) => (
            <React.Fragment key={i}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  py: 1.5,
                }}
              >
                <Typography>{row.label}</Typography>
                <Typography
                  fontWeight={row.label === "Net Pay" ? 600 : 500}
                  sx={{ mr: 5 }}
                >
                  ₹{Number(row.value || 0).toFixed(2)}
                </Typography>
              </Box>
              <Divider />
            </React.Fragment>
          ))}
        </Box>
      </Grid>
    </>
  );
};
